// Playback policy stays independent of Web Audio and the sim's random generator.
import { HERO, type FactionId, type HeroId } from '../sim/defs';
export type Phase = 'menu' | 'early' | 'mid' | 'late';
/** Seconds one music track takes to hand over to the next: the new one rises as the old one falls, both linear from
 *  where they are, so the two never add up past one track at full (2026-10-31, pick music; was 1.5 s in, 0.4 s tail). */
export const CROSSFADE = 0.9;
/** The army whose main theme plays while a pick is open (2026-10-31, Devin: "when you select the army or hero plays
 *  the custom music"): the army card you have on, or your hero's own army (HERO[id].faction in the sim's defs).
 *  Undefined when no pick is open, so the title or the match decides. */
export function choosingArmy(screen: string, army: FactionId | null, hero: HeroId | null): FactionId | undefined {
  if (screen === 'pick') return army ?? undefined;
  if (screen === 'hero' && hero) return HERO[hero].faction;
  return undefined;
}
export type Track = { id: string; file: string; army: string | null; phase: Phase; pick: boolean };
export type Manifest = { sfx: Record<string, string[]>; music: Track[] };
export function takeIndex(count: number, previous: number, random: number): number {
  if (count <= 1) return 0;
  const skip = previous >= 0 && previous < count;
  const n = Math.min(count - (skip ? 2 : 1), Math.floor(Math.max(0, random) * (count - (skip ? 1 : 0))));
  return skip && n >= previous ? n + 1 : n;
}
export function voiceAllowed(copies: number, total: number, now: number, last: number, gap = 0.09) {
  return copies < 4 && total < 24 && now - last >= gap;
}
export function distanceGain(distance: number, focusRadius: number, onScreen = true) {
  const near = Math.max(10, focusRadius * 0.25), far = Math.max(50, focusRadius * 1.3);
  const g = Math.max(0, Math.min(1, (far - distance) / (far - near)));
  return g * g * (onScreen ? 1 : 0.25);
}
export function musicPhase(menu: boolean, tier: number, seconds: number, centerPush = false): Phase {
  return menu ? 'menu' : centerPush || seconds >= 18 * 60 ? 'late' : tier >= 2 ? 'mid' : 'early';
}
export function chooseTrack(tracks: Track[], army: string | null, phase: Phase) {
  const target = phase === 'menu' ? 'early' : phase;
  return tracks.find(t => army ? t.army === army && t.phase === target && (target !== 'early' || t.pick) : t.army === null);
}
export function shouldWarm(sound: 'on' | 'off', loaded: boolean) { return sound === 'on' && !loaded; }
export function nextMusicPhase(phase: Phase, seconds: number): Phase | undefined {
  if (phase === 'early' && seconds >= 250) return 'mid';
  if (phase === 'mid' && seconds >= 1060) return 'late';
  return undefined;
}
export function castKind(hero: string, skill: number) {
  if (skill === 1) return 'cast_ultimate';
  if (hero === 'musashi' || hero === 'lagertha') return 'cast_dash';
  if (hero === 'leonidas' || hero === 'cuauhtemoc') return 'cast_stun';
  if (hero === 'joan' || hero === 'liang') return 'cast_aura';
  return 'cast_area';
}
export function ambienceMix(hour: number, rain: number, snow: number, wind: number, weather: boolean): Record<string, number> {
  const h = ((hour % 24) + 24) % 24;
  const night = h < 5 ? 1 : h < 7 ? (7-h)/2 : Math.max(0, Math.min(1, (h-20)/2));
  const dusk = Math.max(0, 1 - Math.abs(h-20)/2) * (1-night);
  return { meadow_day: 1-night-dusk, dusk, night, rain: weather ? rain : 0, snow: weather ? snow : 0, wind: weather ? Math.max(0, wind-0.4)*0.6 : 0 };
}
